'use client'
import * as React from 'react'
import { Box, Tabs, Tab, useMediaQuery, useTheme, SxProps } from '@mui/material'
import { useRouter } from 'next/navigation'

function samePageLinkNavigation(
	event: React.MouseEvent<HTMLAnchorElement, MouseEvent>
) {
	if (
		event.defaultPrevented ||
		event.button !== 0 || // ignore everything but left-click
		event.metaKey ||
		event.ctrlKey ||
		event.altKey ||
		event.shiftKey
	) {
		return false
	}
	return true
}

interface LinkTabProps {
	label: string
	href?: string
	selected?: boolean
	sx?: SxProps
}

function LinkTab(props: Readonly<LinkTabProps>) {
	const router = useRouter()
	const theme = useTheme()
	const { href, selected, sx } = props
	return (
		<Tab
			component="a"
			onClick={(event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
				if (samePageLinkNavigation(event)) {
					event.preventDefault()
				}
				href && router.push(href)
			}}
			sx={{
				'p': { xs: '18px 20px', sm: '6px 16px' },
				'minHeight': { xs: '48px', sm: '32px' },
				'height': '32px',
				'@media (hover: hover)': {
					'&:hover': {
						backgroundColor: theme.palette.action.hover,
						color: theme.palette.primary.main,
					},
				},
				...sx,
			}}
			aria-current={selected && 'page'}
			aria-selected={selected ? 'true' : 'false'}
			{...props}
		/>
	)
}

interface TabGroupProps {
	ariaLabel: string
	links: LinkTabProps[]
	activeTabIndex: number
	sxStyles?: SxProps
	className?: string
	onTabClick(tabActiveIndex: number): void
	evenlyDistributedOnMobile?: boolean
  }
  
  export default function TabGroup({
	ariaLabel,
	links,
	activeTabIndex,
	sxStyles,
	className,
	onTabClick,
	evenlyDistributedOnMobile = false,
  }: Readonly<TabGroupProps>) {
	const theme = useTheme()
	const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  
	const handleChange = (event: React.SyntheticEvent, newValue: number) => {
	  if (
		event.type !== 'click' ||
		(event.type === 'click' &&
		  samePageLinkNavigation(
			event as React.MouseEvent<HTMLAnchorElement, MouseEvent>
		  ))
	  ) {
		onTabClick?.(newValue)
	  }
	}
  
	const mobileVariant = evenlyDistributedOnMobile ? 'standard' : 'scrollable'
  
	return (
	  <nav>
		<Box sx={{ width: '100%', ...sxStyles }}>
		  <Tabs
			className={className ?? ''}
			value={activeTabIndex === -1 ? false : activeTabIndex}
			onChange={handleChange}
			aria-label={ariaLabel}
			variant={isMobile ? mobileVariant : 'standard'}
			scrollButtons={false}
			sx={{
			  overflowX: {
				xs: evenlyDistributedOnMobile ? 'hidden' : 'auto',
				md: 'visible',
			  },
			  overflowY: 'hidden',
			  touchAction: 'pan-x',
			  scrollBehavior: 'smooth',
			  '& .MuiTabs-flexContainer': {
				whiteSpace: 'nowrap',
				flexWrap: 'nowrap',
				justifyContent: {
				  xs: evenlyDistributedOnMobile ? 'space-evenly' : 'flex-start',
				  md: 'flex-start',
				},
			  },
			  '&::-webkit-scrollbar': {
				height: 0,
			  },
			  '& .MuiTabs-indicator': {
				display: 'flex',
				justifyContent: 'center',
				backgroundColor: theme.palette.surface.neutral.transparent,
				'& > span': {
				  width: 'calc(100% - 30px)',
				  backgroundColor: theme.palette.foreground.brand.primary,
				  borderRadius: '16px 16px 0 0',
				},
			  },
			}}
			TabIndicatorProps={{ children: <span /> }}
		  >
			{links.map(link => (
			  <LinkTab
				key={link.href ?? link.label}
				label={link.label}
				href={link?.href}
				sx={{
				  flex: {
					xs: evenlyDistributedOnMobile ? 1 : '0 0 auto',
					md: '0 0 auto',
				  },
				}}
			  />
			))}
		  </Tabs>
		</Box>
	  </nav>
	)
  }
  